import insertParticipants from '$tablet-app/src/app/calendar/common/insertParticipants';
import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { useUpdateScreenChatContext } from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { TaskItemForm } from '$tablet-app/src/app/components/TaskItemForm/TaskItemForm';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { JSONText } from '$tablet-app/src/app/developer/components/JSONText';
import {
  postDeleteRecordLogEvent,
  postUpdateRecordLogEvent,
} from '$tablet-app/src/app/logging/functions';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import {
  useCurrentUserID,
  useListCurrentHabitatUsers,
} from '$tablet-app/src/app/users';
import { FeatureDomain } from '@akin-home/amplify';
import { ChecklistModel } from '@akin-home/checklists';
import {
  useDeleteTaskList,
  useGetTaskList,
  useListTaskListsByHabitat,
  useUpdateTaskListFromModel,
} from '@akin-home/checklists/frontend';
import { useHabitatID } from '@akin-home/habitats/react';
import { showInfoToast, showSuccessToast } from '@akin-home/toast/native';
import React, { useEffect, useMemo, useState } from 'react';
import { useUpdateChecklistFormCommand } from '../../hooks';

const context = { canSave: true, fields: { title: 'text' } };

const parameters = {
  contact_id: 'id',
};

export const EditChecklistScreen = (
  props: RootStackScreenProps<'EditChecklist'>
) => {
  const {
    navigation: { goBack },
    route: { params },
  } = props;

  const { id } = params;

  const { data: users } = useListCurrentHabitatUsers();
  const user_id = useCurrentUserID();
  const habitatID = useHabitatID();

  const { list, isLoading } = useGetTaskList({ id });
  const { lists, isSuccess } = useListTaskListsByHabitat({
    habitatID,
  });

  const [checklist, setChecklist] = useState<ChecklistModel | undefined>(list);

  useUpdateScreenChatContext(context);
  useUpdateChecklistFormCommand(setChecklist);

  const update = useUpdateTaskListFromModel();
  const delete_checklist = useDeleteTaskList();

  const show_main_shopping_list_checkbox =
    checklist?.primaryShoppingList ||
    (checklist?.type === 'Shopping' &&
      isSuccess &&
      !lists.some((l) => l.primaryShoppingList));

  const onSave = async () => {
    if (!checklist) return;

    showInfoToast({ text1: 'Saving List' });
    goBack();

    await update(checklist);

    postUpdateRecordLogEvent({
      user_id,
      feature_domain: FeatureDomain.CHECKLISTS,
      data: {
        id,
        model: 'Checklist',
      },
    });

    showSuccessToast({ text1: 'List Saved!' });
  };

  const onDelete = async () => {
    if (!checklist) return;

    showInfoToast({ text1: 'Deleting List' });

    goBack();

    await delete_checklist(checklist.id);

    postDeleteRecordLogEvent({
      user_id,
      feature_domain: FeatureDomain.CHECKLISTS,
      data: {
        id,
        model: 'Checklist',
      },
    });

    showSuccessToast({ text1: 'List Deleted' });
  };

  const listNames = useMemo(
    () =>
      lists.length > 0
        ? lists.map((list) => list.title.toLowerCase().trim())
        : [],
    [lists]
  );

  useEffect(() => {
    if (!checklist && list) {
      setChecklist(insertParticipants(users)(list));
      sendBackgroundMessage(intent_labels.open_edit_checklist_screen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, checklist]);

  if (isLoading || !checklist) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <TaskItemForm
        mode="edit"
        users={users}
        item={checklist}
        show_main_shopping_list_checkbox={show_main_shopping_list_checkbox}
        listNames={listNames}
        onSave={onSave}
        onChange={setChecklist}
        onCancel={goBack}
        onDelete={onDelete}
      />

      <DeveloperScreenInfo
        context={<JSONText>{context}</JSONText>}
        parameters={<JSONText>{parameters}</JSONText>}
      />
    </>
  );
};
